import styled, { css, keyframes, createGlobalStyle } from "styled-components";

interface BtnProps {
  active?: boolean;
  small?: boolean;
}

interface OptionItemProps {
  color: string;
}

interface OptionItemIconProps {
  color: string;
}

// Add a subtle pulse animation for active buttons
const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(74, 134, 232, 0.2); }
  70% { box-shadow: 0 0 0 6px rgba(74, 134, 232, 0); }
  100% { box-shadow: 0 0 0 0 rgba(74, 134, 232, 0); }
`;

// Animação para entrada suave
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

// Animação para zoom suave
const zoomIn = keyframes`
  from {
    transform: scale(0.98);
    opacity: 0.8;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

// Add more subtle animations for better UX
const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  background: #ffffff;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  padding: 0.7rem;
  margin-bottom: 0.75rem;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
  }

  &.fullscreen-toolbar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1001;
    background-color: #f8f9fa;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-radius: 0;
    padding: 10px 20px;
    transition: all 0.3s ease;
    animation: ${fadeIn} 0.3s ease-out;
  }
`;

export const Group = styled.div`
  display: flex;
  gap: 0.25rem;
  position: relative;
`;

export const Btn = styled.button<BtnProps>`
  ${({ small }) =>
    small &&
    css`
      font-size: 0.7rem;
      padding: 0.25rem 0.4rem;
    `}
  background: ${({ active }) => (active ? "#f23b5d1e" : "#fff")};
  border: 1px solid ${({ active }) => (active ? "#f23b5d" : "#EEEEEE")};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease, transform 0.1s ease;
  color: ${({ active }) => (active ? "#f23b5d" : "#717171")};
  position: relative;

  &:hover {
    background: ${({ active }) => (active ? "#f23b5d1e" : "#F7F7F7")};
    border-color: ${({ active }) => (active ? "#f23b5d" : "#DDDDDD")};
    transform: translateY(-1px);
    z-index: 1;
  }

  &:active {
    transform: translateY(0);
  }

  ${({ active }) =>
    active &&
    css`
      animation: ${pulse} 2s 1;
    `}

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(242, 59, 93, 0.2);
  }
`;

export const IconBtn = styled(Btn)`
  width: 25px;
  height: 30px;
  display: flex;  
  align-items: center;   
  justify-content: center;
  font-size: 13px;
  border-radius: 6px;
  position: relative;
  min-width: 32px;

  /* Tooltip sempre abaixo do elemento */
  &::before {
    content: attr(data-tooltip);
    position: absolute;
    top: calc(100% + 5px);
    left: 50%;
    transform: translateX(-50%) translateY(-5px);
    padding: 4px 8px;
    background: #333;
    color: white;
    font-size: 12px;
    white-space: nowrap;
    border-radius: 4px;
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s ease;
    pointer-events: none;
    z-index: 1000;
  }

  &::after {
    content: "";
    position: absolute;
    top: calc(100% + 1px);
    left: 50%;
    transform: translateX(-50%) translateY(-5px);
    border: 4px solid transparent;
    border-top-color: transparent;
    border-bottom-color: #333;
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s ease;
    pointer-events: none;
    z-index: 1000;
  }

  /* Manter suporte para data-tooltip-bottom para compatibilidade */
  &[data-tooltip-bottom]::before {
    content: attr(data-tooltip-bottom);
  }

  &:hover::before,
  &:hover::after,
  &[data-tooltip-bottom]:hover::before,
  &[data-tooltip-bottom]:hover::after {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
  }

  &.exit-fullscreen-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 35px;
    height: 35px;
    border-radius: 50%;
    background-color: rgba(240, 240, 240, 0.8);
    box-shadow: 0 1px 5px rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    border: none;

    &:hover {
      background-color: rgba(220, 220, 220, 0.9);
    }
  }
`;

export const DropdownBtn = styled(IconBtn)`
  min-width: 40px;
  width: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 8px 12px;

  span + span {
    font-size: 12px;
    margin-left: 6px;
  }

  svg {
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: scale(1.1);
  }
`;

export const Divider = styled.div`
  width: 1px;
  height: 24px;
  background-color: #eeeeee;
  margin: 0 6px;
  align-self: center;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #dddddd;
  }
`;

export const OptionItemIcon = styled.div<OptionItemIconProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 40px;
  min-height: 40px;
  background-color: #f23b5d;
  color: white;
  border-radius: 5px;
  font-size: 13px;

  div {
    display: flex;
    font-size: 16px !important;

    svg {
      margin-right: 0px !important;
    }
  }

  ${(props) =>
    props.color &&
    css`
      background-color: ${props.color + "1e"};

      svg {
        color: ${props.color};
        margin-right: 0px !important;
      }
    `}
`;

export const DropdownContent = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 150; // Increased z-index to be above toolbars
  background: white;
  border-radius: 12px; // Increased border radius
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  padding: 0.5rem 0;
  min-width: 160px;
  margin-top: 5px;
  animation: ${fadeInScale} 0.25s cubic-bezier(0.2, 0, 0, 1); // Smoother animation curve
  border: 1px solid rgba(0, 0, 0, 0.05);
  max-height: 80vh;
  overflow-y: auto;
  transition: all 0.2s ease; // Add transition for smoother interactions

  &.highlight-dropdown {
    z-index: 151;
  }

  &.link-dropdown {
    z-index: 152;
  }

  &.bubble-dropdown {
    z-index: 155; // Even higher for bubble menu dropdowns
  }

  &.portal-dropdown {
    position: fixed;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15); // Enhanced shadow
    background: white;
    border-radius: 12px;
    animation: ${fadeInScale} 0.2s cubic-bezier(0.2, 0, 0, 1); // Use scale animation
    z-index: 1000; // Ensure it's higher than most elements
  }

  &.style-dropdown {
    animation: ${fadeInScale} 0.2s cubic-bezier(0.2, 0, 0, 1);
    font-family: Circular, -apple-system, BlinkMacSystemFont, Roboto,
      "Helvetica Neue", sans-serif;

    .menu-item {
      transition: all 0.2s ease; // Smoother transition

      &:hover {
        background-color: #f7f7f7;
      }

      &.danger-item:hover {
        background-color: rgba(255, 90, 95, 0.15);
      }
    }
  }

  &.dynamic-fields-dropdown {
    // Specific styling for dynamic fields dropdown
    padding: 8px 0;
    border: 1px solid rgba(0, 0, 0, 0.08);

    // Custom scrollbar for better UX
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.1);
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: rgba(0, 0, 0, 0.2);
    }
  }

  &.airbnb-dropdown {
    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  }

  .airbnb-menu-group {
    .airbnb-dropdown-item {
      transition: all 0.2s ease; // Enhance transition

      &:hover {
        background-color: #f7f7f7;
      }

      &.airbnb-danger {
        color: #ff385c;

        &:hover {
          background-color: rgba(255, 56, 92, 0.07);
        }
      }
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const EditorBox = styled.div`
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  min-height: 100px;
  padding: 1rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
  cursor: text;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  position: relative;
  overflow: hidden;

  /* Ensure the ProseMirror instance has the correct stacking */
  .ProseMirror {
    z-index: 1;
  }

  /* Ensure contextual controls have higher z-index than editor but lower than menus */
  .contextual-button {
    z-index: 50;
  }

  &:hover {
    border-color: #d0d0d0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
  }

  &:focus-within {
    border-color: #ff0000;
    box-shadow: 0 0 0 2px rgba(74, 134, 232, 0.1);
  }

  .ProseMirror:focus {
    outline: none;
  }

  table {    
    border-collapse: collapse;
    max-width: 100%;
    width: 100%;
    min-width: 100%;
  }

  th,
  td {
    border: 1px solid #ddd;
    padding: 4px 6px;
  }

  .ProseMirror {
    &.resize-cursor {
      cursor: col-resize;
    }

    .column-resize-handle {
      pointer-events: auto;
      background: transparent;
      transition: background 120ms, width 120ms;
    }
    .column-resize-handle:hover,
    .column-resize-handle.active {
      background: #0099ff;
      width: 8px;
      cursor: col-resize;
    }

    .column-resize-handle:hover::before {
      content: "";
      position: absolute;
      top: 0;
      bottom: -2px;
      left: -2px;
      width: 1px; /* valor inicial */
      background: #0099ff;
    }

    .column-resize-handle:hover td,
    .column-resize-handle:hover th {
      border-right: 3px solid #0099ff;
    }

    table {
      border-collapse: collapse;
      margin: 0;
      overflow: hidden;
      table-layout: fixed;
      max-width: 100%;
      min-width: 100%;
      width: 100%;

      td,
      th {
        border: 1px solid gray;
        box-sizing: border-box;
        min-width: 1em;
        padding: 6px 8px;
        position: relative;
        vertical-align: top;

        > * {
          margin-bottom: 0;
        }
      }

      th {
        background-color: #f0f0f0;
        font-weight: bold;
        text-align: left;
      }

      .selectedCell:after {
        background: rgb(20, 90, 255, 0.2);
        content: "";
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        pointer-events: none;
        position: absolute;
        z-index: 2;
      }

      .column-resize-handle {
        background-color: purple;
        bottom: -2px;
        pointer-events: none;
        position: absolute;
        right: -2px;
        top: 0;
        width: 4px;
      }
    }
  }

  .tableWrapper {
    margin: 1.5rem 0;
    overflow-x: auto;
  }

  &.resize-cursor {
    cursor: ew-resize;
    cursor: col-resize;
  }

  ul,
  ol {
    padding: 0 1rem;
    margin: 1.25rem 1rem 1.25rem 0.4rem;

    li p {
      margin-top: 0.25em;
      margin-bottom: 0.25em;
    }
  }

  mark {
    background-color: #faf594;
    color: black; /* Ensure text color is black for better visibility */
    border-radius: 0.4rem;
    box-decoration-break: clone;
    padding: 0.1rem 0.3rem;
  }

  /* Add styling for white highlight backgrounds */
  mark[data-color="#FFFFFF"] {
    background-color: #ffffff;
    outline: 1px solid #eeeeee;
  }

  /* Add styling for indentation */
  p[style*="padding-left"],
  h1[style*="padding-left"],
  h2[style*="padding-left"],
  h3[style*="padding-left"],
  h4[style*="padding-left"] {
    transition: padding-left 0.2s ease;
  }

  .indent-1 {
    padding-left: 2em !important;
  }
  .indent-2 {
    padding-left: 4em !important;
  }
  .indent-3 {
    padding-left: 6em !important;
  }
  .indent-4 {
    padding-left: 8em !important;
  }
  .indent-5 {
    padding-left: 10em !important;
  }

  /* Improve list indentation */
  ul,
  ol {
    padding-left: 2em;

    li {
      padding: 0.2em 0;
    }

    ul,
    ol {
      margin-top: 0.2em;
      margin-bottom: 0.2em;
    }
  }

  .tiptap p.is-empty::before {
    color: #adb5bd;
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
  }

  &.editor-fullscreen {
    height: 100%;
    max-height: none;
    margin: 0 auto;
    border-radius: 0;
    padding: 30px 40px;
    max-width: 900px;
    width: 100%;
    background-color: white;
    box-shadow: 0 0 40px rgba(0, 0, 0, 0.1);
    position: relative;
    transition: all 0.3s ease;
    animation: ${zoomIn} 0.3s ease-out;
    overflow: auto;
    margin-top: 20px;
    margin-bottom: 10px;
    border: none;

    .ProseMirror {
      min-height: calc(100vh - 200px);
      transition: all 0.3s ease;
    }
  }

  .exit-fullscreen-btn {
    position: fixed;
    top: 8px;
    right: 8px;
    width: 40px;
    height: 40px;
    z-index: 1010;
    border-radius: 50%;
    background-color: rgba(240, 240, 240, 0.9);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    transition: all 0.2s ease;

    &:hover {
      background-color: rgba(220, 220, 220, 1);
      transform: scale(1.05);
    }

    &:active {
      transform: scale(0.95);
    }
  }
`;

export const OptionItemDescription = styled.div`
  margin-left: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  h3 {
    font-size: 12px;
    color: black;
    text-align: left;
    margin-bottom: 3px;
    display: flex;
    justify-content: center;
    align-items: center;

    span {
      background-color: #00bcd4;
      color: white;
      border-radius: 10px;
      margin-left: 5px;
      padding: 3px 7px;
      font-size: 10px;
    }
  }

  span {
    font-size: 11px;
    color: grey;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 230px;
  }
`;

export const OptionItem = styled.div<OptionItemProps>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin: 5px;
  margin-bottom: 10px;
  margin-left: 15px;
  border-radius: 5px;
  cursor: pointer;
  user-select: none;

  :hover {
    background-color: #f23b5d1e;

    ${(props) =>
    props.color &&
    css`
        background-color: ${props.color}1e;
      `}
  }
`;

export const OptionTitle = styled.h3`
  color: gray;
  font-size: 12px;
  text-align: left;
  font-weight: 500;
  user-select: none;
  margin-bottom: 10px;
  margin-left: 5px;
`;

export const RemoveHighlightBtn = styled.button`
  background: white;
  border: 1px solid #eeeeee;
  margin: 0 8px 8px 8px;
  padding: 8px 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #717171;
  font-size: 13px;
  text-align: center;
  width: calc(100% - 16px);

  &:hover {
    background: #f7f7f7;
    border-color: #dddddd;
    color: #333;
  }

  &:active {
    background: #eeeeee;
    transform: translateY(1px);
  }
`;

export const DropdownItem = styled.button<{ active?: boolean }>`
  padding: 8px 14px;
  cursor: pointer;
  color: ${(props) => (props.active ? "#0066CC" : "#717171")};
  background: ${(props) => (props.active ? "#F0F7FF" : "transparent")};
  display: flex;
  align-items: center;
  font-size: 14px;
  transition: all 0.2s ease; // Smoother transitions
  overflow: hidden;
  border: none;
  border-radius: 6px; // Added border radius for better look
  margin: 0 4px; // Added margin for better spacing

  ${(props) => (props.active ? "#4A86E8" : "transparent")}

  &:hover {
    background-color: #f7f7f7;
    color: #333;
    transform: translateX(2px); // Subtle movement on hover
  }

  &:active {
    transform: scale(0.98); // Subtle press effect
  }

  span {
    margin-right: 8px;
  }

  svg {
    margin-right: 8px;
    color: ${(props) => (props.active ? "#0066CC" : "#717171")};
    transition: all 0.2s ease; // Add transition for icon
  }

  &.danger {
    color: #e53935;
  }

  &.menu-item {
    display: flex;
    align-items: center;
    width: 100%;
    font-size: 14px;
    color: #222222;
  }

  &.airbnb-dropdown-item {
    border-radius: 8px;
    margin: 0 6px;
    transition: all 0.2s ease;

    &:active {
      transform: scale(0.98);
    }
  }

  &.field-dropdown-item {
    &:hover {
      background-color: #f0f7ff; // Light blue for fields

      svg {
        transform: scale(1.1);
      }
    }
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

// Enhance the ColorPicker with better transitions
export const ColorPicker = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-gap: 8px;
  padding: 10px;

  > div {
    width: 24px;
    height: 24px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275); // Bouncy effect
    border: 1px solid #eeeeee;

    &:hover {
      transform: scale(1.15);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      z-index: 1;
    }

    &.active {
      border: 2px solid #4a86e8;
      box-shadow: 0 0 0 2px rgba(74, 134, 232, 0.2);
    }
  }
`;

// Add style for the dynamic fields button to make it more prominent
export const DynamicFieldBtn = styled(DropdownBtn)`
  background-color: #f8f9fa;
  border-color: #e0e0e0;

  &:hover {
    background-color: #f0f0f0;
    border-color: #d0d0d0;
  }

  &:active {
    background-color: #e8e8e8;
  }

  // Add a subtle pulsing effect for the button
  @keyframes gentle-pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.05);
    }
    70% {
      box-shadow: 0 0 0 4px rgba(0, 0, 0, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
    }
  }

  svg {
    color: #555;
    margin-right: 6px;
    animation: gentle-pulse 2s infinite;
  }
`;

// Add smoother animations for dropdowns
export const GlobalStyle = createGlobalStyle`
  .tiptap-editor-fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    background-color: #f0f2f5;
    display: flex;
    flex-direction: column;
    padding-top: 60px;
    overflow: hidden;
    transition: all 0.3s ease;    
  }
  
  /* Estilo da barra de rolagem personalizada para o editor em tela cheia */
  .editor-fullscreen .ProseMirror::-webkit-scrollbar {
    width: 8px;
  }
  
  .editor-fullscreen .ProseMirror::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
  }
  
  .editor-fullscreen .ProseMirror::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
  
  .editor-fullscreen .ProseMirror::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
  }
  
  /* Add keyframe animations for smoother transitions */
  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* Add styling for dynamic field tags inside editor */
  .field-dynamic-tag {
    display: inline-flex;
    align-items: center;
    background-color: #f0f7ff;
    color: #0066cc;
    border-radius: 4px;
    padding: 2px 6px;
    margin: 0 2px;
    font-size: 0.9em;
    border: 1px solid #d0e0fc;
    user-select: none;
    white-space: nowrap;
    transition: all 0.2s ease;
    cursor: default;
    
    &:hover {
      background-color: #e0f0ff;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
  }
  
  /* Enhance the menu animations */
  .dynamic-fields-dropdown {
    animation: fadeInDown 0.2s cubic-bezier(0.2, 0, 0, 1);
  }
`;
